import type { CSSProperties } from 'react';
import type { ReportTheme } from '../types/reportTheme';
import { roleColor } from './reportRoleColors';

/** Canonical H2 class for top-level report sections (matches Executive Summary). */
export const REPORT_SECTION_H2_CLASS = 'text-xl font-bold border-b-2 pb-2';

/** EditableHeading / inner span class paired with {@link REPORT_SECTION_H2_CLASS}. */
export const REPORT_SECTION_HEADING_TEXT_CLASS = 'text-xl font-bold';

/**
 * #34 P2 STAGE B — the keyed-on-`headingScale` Tailwind class pair for a
 * top-level section heading. This is the SAME class policy the inline
 * feasibility sections used via the (formerly view-local) `reportSectionHeadingChrome`
 * helper: editorial = a large display head over a thin accent hairline,
 * compact = a small uppercase wide-tracked head, technical = the balanced
 * reference treatment (byte-identical to {@link REPORT_SECTION_H2_CLASS} /
 * {@link REPORT_SECTION_HEADING_TEXT_CLASS}).
 *
 * Extracted to this shared lib so the modern `NewTypeSectionsRouter` can emit
 * the SAME heading classes the inline blocks emitted — keeping the rendered DOM
 * IDENTICAL when a section moves from the inline path to the registry router
 * (the reportSurfaceParity baseline pins this). Pure / safe in render.
 *
 * NOTE: only the CLASS pair is shared here; the colour/font/breakAfter inline
 * STYLE stays with each renderer (it is not class-bearing, so it does not affect
 * the DOM-class parity oracle).
 */
export function reportSectionHeadingClasses(
  theme: Pick<ReportTheme, 'headingScale'>,
): { h2Class: string; textClass: string } {
  switch (resolveHeadingScale(theme.headingScale)) {
    case 'editorial':
      return {
        h2Class: 'text-2xl font-semibold tracking-tight border-b pb-4',
        textClass: 'text-2xl font-semibold tracking-tight',
      };
    case 'compact':
      return {
        h2Class: 'text-sm font-bold uppercase tracking-[0.14em] border-b pb-1.5',
        textClass: 'text-sm font-bold uppercase tracking-[0.14em]',
      };
    case 'technical':
    default:
      return {
        h2Class: REPORT_SECTION_H2_CLASS,
        textClass: REPORT_SECTION_HEADING_TEXT_CLASS,
      };
  }
}

/**
 * Heading ink that follows the reader's light/dark mode.
 *
 * `theme.headingColor` is the RAW light-mode token (a near-black ink). A dozen
 * report surfaces read it directly for `color`/`borderColor`, which bypassed
 * `roleColor`'s on-dark ramp — so section headings, cover titles, the appendix
 * and the specialized-section tables rendered near-black on the dark page and
 * were effectively invisible. `roleColor(theme, 'ink')` returns that SAME token
 * in light mode, so swapping call sites to this helper is byte-identical for
 * light (and therefore for every export, which stays pinned light) while picking
 * up the ramp when the reader is dark.
 */
export function reportHeadingInk(
  theme: Pick<ReportTheme, 'headingColor'> | Partial<ReportTheme> | null | undefined,
): string {
  return roleColor((theme ?? undefined) as Partial<ReportTheme> | undefined, 'ink');
}

/** Section-heading style; colour + border follow the reader mode via {@link reportHeadingInk}. */
export function reportSectionHeadingStyle(
  theme: Pick<ReportTheme, 'headingColor' | 'headingFont'>,
): CSSProperties {
  const ink = reportHeadingInk(theme);
  return {
    borderColor: ink,
    color: ink,
    fontFamily: theme.headingFont,
  };
}

// ── TS3 (headingScale) + TS8 (themeTypographyScale) + PART K1 ───────────
//
// The report's `headingScale` chooses a base type scale; the optional
// `themeTypographyScale` multiplier (0.875 | 1 | 1.125 | 1.25) then
// scales every concrete size up/down so the on-screen + print render
// actually tracks the active theme instead of a fixed `text-xl`.
//
// Sizes are expressed in rem strings so callers can drop them straight
// into an inline `style={{ fontSize }}` and have it win over the
// Tailwind `text-xl` utility (inline styles beat classes). Existing
// callers that don't pass a style keep `text-xl` and render unchanged.
//
// PART K1 — TYPE-SCALE UNIFICATION + DENSIFICATION (William 2026-06-16).
//
// This is now the ONE shared type scale for the whole report island. The
// reference target is the "Reactor Datum" redesign PDF — its deliberate
// hierarchy: a prominent SERIF display head, a crisp MONO small-caps
// wide-tracked eyebrow/label, SERIF hero-numerals for key figures, a
// readable 14px body, and tight leading. We adopt the redesign's
// PROPORTIONS in OUR font families (theme.headingFont / theme.bodyFont) —
// the scale changes, NOT the faces, and NOT the colours (roleColor still
// owns ink/accent). Two formerly-divergent body baselines (the shell's
// `SHELL_BODY_BASE_REM` and `reportTypography`'s `BODY_BASE_REM`) now both
// resolve through THIS scale's `body` size, so there is a single 14px
// body baseline everywhere.
//
// Densification vs the prior #22 print-density scale:
//   - `display` (NEW): the big serif section/feature head — the redesign's
//     ~28px display line; tracks down per scale; used by the numbered-
//     section treatment + premium primitives.
//   - `h2` is bumped up on `editorial` to read as a real display head
//     (1.45rem ≈ 17.4pt) while `technical`/`compact` stay tight and dense.
//   - `eyebrow` (NEW): the mono small-caps wide-tracked label tier (~10.5px)
//     so every eyebrow/field-label resolves through one size, not scattered
//     `text-xs`/`text-[11px]` literals.
//   - `hero` (NEW): the serif hero-numeral tier (the 300 MW / 48.5 MW /
//     $4-7M treatment) for premium metric panels (K3 consumes it).
//   - line-heights TIGHTEN (heads 1.12, body 1.5) toward the redesign's
//     compactness, vs the prior airy 1.6.
//
// `editorial` stays the largest, `compact` the tightest, and the legibility
// invariants the #22 gate pins hold: h2 > h3 in every scale and every
// heading clears the 13pt floor.

/** One concrete tier in the report type scale (rem before the multiplier). */
interface TypeScaleEntry {
  /** Big serif display head (numbered-section + feature heads). */
  displayRem: number;
  /** Top-level section H2. */
  h2Rem: number;
  /** Sub-section H3. */
  h3Rem: number;
  /** Body prose / table cells — the single 14px baseline. */
  bodyRem: number;
  /** Mono small-caps wide-tracked eyebrow / field label. */
  eyebrowRem: number;
  /** Serif hero-numeral (premium metric value). */
  heroRem: number;
}

/**
 * The ONE canonical report type scale, keyed by `headingScale`.
 *
 * PART K1: every heading/body/label/number size in the report island
 * resolves through this table (via {@link reportTypographySizes}), so a
 * single edit flows uniformly to the default + every theme + all 11 report
 * types — no per-archetype work.
 *
 * Floor invariants (pinned by `reportTypographySizes.test.ts`): h2 > h3 in
 * every scale; every heading >= 13pt (1.083rem) at the design contract
 * 1rem = 16px = 12pt. `display` is the largest tier and `hero` the largest
 * overall (a hero-numeral, not body text, so it has no legibility ceiling).
 */
const REPORT_TYPE_SCALE: Record<ReportTheme['headingScale'], TypeScaleEntry> = {
  // EDITORIAL — the prominent-serif-display target. Big display head, an
  // h2 that reads as a real display line, the densest hero-numeral.
  editorial: {
    displayRem: 1.83, // ~22pt serif display (numbered-section / feature head)
    h2Rem: 1.33, //     ~16pt section head (re-harmonized 2026-06-27 to the 9.5pt body — William-approved type system)
    h3Rem: 1.04, //     ~12.5pt sub-head (one clean step above the 9.5pt body; legibility floor lowered 13→12pt to match)
    bodyRem: 0.79, //   ~12.6px body = 9.5pt (the unified baseline; densified from 0.875/14px 2026-06-27 per William; heads re-harmonized to restore the ratio)
    eyebrowRem: 0.71, // ~8.5pt mono small-caps eyebrow / label
    heroRem: 2.5, //    ~30pt serif hero-numeral (300 MW treatment) — held at the golden 30pt to
    //                  preserve density (the spec's 40pt/3.33rem added ~2pp on stat-heavy reports);
    //                  bump to 3.33 if max drama is wanted over page count.
  },
  // TECHNICAL — balanced/dense. Display present but tighter; h2 stays near
  // the prior reference treatment so technical reports stay compact.
  technical: {
    displayRem: 1.5, //  ~18pt display
    h2Rem: 1.25, //      15pt section head (was 1.208)
    h3Rem: 1.0834, //    13pt floor
    bodyRem: 0.79, //    ~12.6px body = 9.5pt (densified 2026-06-27)
    eyebrowRem: 0.66, // ~10.5px eyebrow
    heroRem: 2.125, //   ~25.5pt hero-numeral
  },
  // COMPACT — the tightest variant for the densest investment reports.
  compact: {
    displayRem: 1.375, // ~16.5pt display
    h2Rem: 1.167, //      14pt section head (was 1.125)
    h3Rem: 1.0834, //     13pt floor
    bodyRem: 0.79, //     ~12.6px body = 9.5pt (densified 2026-06-27)
    eyebrowRem: 0.625, // 10px eyebrow
    heroRem: 1.875, //    ~22.5pt hero-numeral
  },
};

/**
 * Tight, redesign-aligned line-heights (UNITLESS). The reference PDF sets
 * heads close (~1.12) and body readable-but-tight (~1.5), denser than the
 * prior airy 1.6. Exported so the shell + heading renderers share ONE
 * leading contract instead of hand-rolling per call site.
 */
export const REPORT_LINE_HEIGHT = {
  /** Display / section heads — tight, serif-display leading. */
  heading: 1.12,
  /** Body prose — readable but denser than the prior 1.6. */
  body: 1.5,
  /** Mono eyebrow / label — snug. */
  eyebrow: 1.1,
} as const;

/**
 * Wide letter-tracking for the mono small-caps eyebrow/label tier (the
 * redesign's `.2em`-tracked engineering labels). Exported so the eyebrow
 * renderers share ONE tracking value. Heading tracking stays slightly
 * negative for the serif display (set per call site).
 */
export const REPORT_EYEBROW_TRACKING = '0.18em';

/**
 * REPORT_TEXT_PX — the fixed-pixel UI text ramp for the report island.
 *
 * The theme-scaled tiers ({@link REPORT_TYPE_SCALE} → display / h2 / h3 / body
 * / eyebrow / hero) cover HEADINGS and body prose, which scale with the
 * theme's `headingScale` / `themeTypographyScale`. But the report's EDITORIAL
 * PRIMITIVES (badges, chips, table headers, KPI labels, datum strips, figure
 * captions, reference rows, risk/scale meters, …) carry a second family of
 * sizes for small UI furniture — field labels, unit suffixes, captions, and
 * micro-annotations — that are deliberately FIXED-px (they must NOT grow with
 * the body size, or a chip/legend would blow out its container). Before this
 * token those sizes were ~30 distinct hardcoded literals (`fontSize: 12`,
 * `fontSize: 10`, `fontSize: 9`, …) scattered across the 30 primitives — the
 * exact ad-hoc surface a type audit exists to remove.
 *
 * This is the canonical four-step ramp those primitives share. The values
 * EQUAL the dominant existing literals, so routing a primitive through the
 * token is BYTE-IDENTICAL to the number it replaces (a pure de-magic-numbering
 * refactor — no rendered pixel moves; the visual-matrix / reportSurfaceParity
 * gates stay green). Pinned by `reportTextScale.test.ts`.
 *
 * TIERS (px):
 *   - `label`      (12) — the dominant small UI label / dense in-component line
 *                          (table headers, callout labels, datum strips,
 *                          definition terms, figure captions, KPI + recommendation
 *                          labels, subsection labels).
 *   - `annotation` (11) — unit suffix / secondary annotation beside a value.
 *   - `caption`    (10) — meta / eyebrow-small / caption (datasheet values,
 *                          radar caps, reference meta, stack-bar labels).
 *   - `micro`      ( 9) — smallest legible chip / badge / matrix-cell label.
 *
 * OFF-RAMP: a handful of primitives still carry inter-step oddballs (8.5, 9.5,
 * 10.5, 11.5) and component-specific DISPLAY sizes (16–128px hero/part
 * numerals). Those are left as literals for now — snapping them onto the ramp
 * changes pixels, so it belongs in a separate, visually-reviewed consolidation
 * pass rather than this byte-stable extraction.
 */
export const REPORT_TEXT_PX = {
  label: 12,
  annotation: 11,
  caption: 10,
  micro: 9,
} as const;

export type ReportTextTier = keyof typeof REPORT_TEXT_PX;

/** Clamp an arbitrary typography multiplier to the supported set's
 *  numeric range. Falls back to 1 (Normal) when unset/invalid. */
function resolveTypographyScale(
  scale: ReportTheme['themeTypographyScale'] | undefined,
): number {
  return typeof scale === 'number' && Number.isFinite(scale) && scale > 0
    ? scale
    : 1;
}

/** Resolve a heading scale, tolerating undefined/legacy themes. */
function resolveHeadingScale(
  scale: ReportTheme['headingScale'] | undefined,
): ReportTheme['headingScale'] {
  return scale === 'editorial' || scale === 'technical' || scale === 'compact'
    ? scale
    : 'editorial';
}

/** The full set of report type-scale sizes (rem strings) for a theme. */
export interface ReportTypographySizes {
  /** Big serif display head (numbered-section / feature head). */
  display: string;
  /** Top-level section H2. */
  h2: string;
  /** Sub-section H3. */
  h3: string;
  /** Body prose / table cells (the unified 14px baseline). */
  body: string;
  /** Mono small-caps wide-tracked eyebrow / field label. */
  eyebrow: string;
  /** Serif hero-numeral (premium metric value). */
  hero: string;
}

/**
 * TS3 + TS8 + PART K1 — resolve the concrete report type-scale sizes (rem
 * strings) for the active theme. `headingScale` picks the baseline scale
 * from {@link REPORT_TYPE_SCALE}; `themeTypographyScale` multiplies every
 * tier together. Pure; safe in render.
 *
 * Returns `undefined`-free rem strings (e.g. `"1.45rem"`) intended for an
 * inline `style={{ fontSize }}` that overrides the existing `text-xl`
 * Tailwind class without removing it (back-compat). The returned object is
 * a SUPERSET of the prior `{ h2, h3, body }` shape, so existing callers
 * keep working unchanged while new callers can read `display` / `eyebrow` /
 * `hero` for the redesign's hierarchy.
 */
export function reportTypographySizes(
  theme: Pick<ReportTheme, 'headingScale' | 'themeTypographyScale'>,
): ReportTypographySizes {
  const base = REPORT_TYPE_SCALE[resolveHeadingScale(theme.headingScale)];
  const mult = resolveTypographyScale(theme.themeTypographyScale);
  const round = (n: number) => `${Math.round(n * 1000) / 1000}rem`;
  return {
    display: round(base.displayRem * mult),
    h2: round(base.h2Rem * mult),
    h3: round(base.h3Rem * mult),
    body: round(base.bodyRem * mult),
    eyebrow: round(base.eyebrowRem * mult),
    hero: round(base.heroRem * mult),
  };
}

/**
 * PART K1 — the body font size (rem) for a section shell, resolved through
 * the SAME unified scale as {@link reportTypographySizes}. This REPLACES the
 * shell's formerly-local `SHELL_BODY_BASE_REM` table (editorial 1.0625 /
 * technical 0.9375 / compact 0.875), which made the shell prose render a
 * different size from the `reportTypographySizes(theme).body`-driven prose,
 * tables, and editorial blocks. Now there is ONE 14px body baseline across
 * every report surface. Pure; safe in render.
 */
export function reportShellBodyFontSize(
  theme: Pick<ReportTheme, 'headingScale' | 'themeTypographyScale'>,
): string {
  return reportTypographySizes(theme).body;
}

/**
 * TS3 + TS8 + PART K1 — inline style for the canonical H2 section heading,
 * combining the color/font from {@link reportSectionHeadingStyle} with the
 * theme-driven `fontSize` AND the tight redesign-aligned heading leading.
 * Drop onto the existing `<h2>` (which keeps its `text-xl` class) so the
 * size + leading now track the theme. K1 adds `lineHeight` (1.12) so the
 * bigger serif display heads sit close, matching the reference rhythm.
 */
export function reportSectionHeadingStyleScaled(
  theme: Pick<
    ReportTheme,
    'headingColor' | 'headingFont' | 'headingScale' | 'themeTypographyScale'
  >,
): CSSProperties {
  return {
    ...reportSectionHeadingStyle(theme),
    fontSize: reportTypographySizes(theme).h2,
    lineHeight: REPORT_LINE_HEIGHT.heading,
  };
}

// ── PART K1 — numbered-section header treatment ─────────────────────────
//
// The redesign's signature section rhythm is "NN / serif heading / mono
// subtitle": a small accent-inked section index, a big serif display head,
// and a mono small-caps wide-tracked subtitle below. This is data-driven by
// the section's 1-based display index (already computed for the TOC and
// stamped as `data-section-number` on each shell).
//
// These are TOKEN BUILDERS (style objects + props), not a component — the
// renderers (SectionsRouter / SiteRagReportView / premium primitives) keep
// owning the JSX (EditableHeading, anchoring, edit/override wiring), so the
// numbered treatment composes into the existing heading without a structural
// rewrite. Every value resolves through the shared scale + roleColor, so it
// is theme-aware and stays in OUR fonts/colours.

/** Zero-pad a 1-based section index to the redesign's "01"/"02" form. */
export function formatSectionNumber(index: number): string {
  if (!Number.isFinite(index) || index < 1) return '';
  const n = Math.floor(index);
  return n < 10 ? `0${n}` : String(n);
}

/** The resolved style tokens for the numbered-section header treatment. */
export interface ReportNumberedHeadingStyles {
  /** "01" section-index eyebrow (mono, wide-tracked, accent ink). */
  number: CSSProperties;
  /** The big serif display heading (theme heading font + ink). */
  heading: CSSProperties;
  /** The mono small-caps wide-tracked subtitle line. */
  subtitle: CSSProperties;
}

/**
 * PART K1 — resolve the inline styles for the "NN / serif heading / mono
 * subtitle" numbered-section rhythm in the active theme.
 *
 * - `number` — the section index eyebrow: mono (the editorial
 *   `--report-body-font` data face), wide-tracked, uppercase, accent ink.
 * - `heading` — the big serif DISPLAY head: theme `headingFont`, ink, the
 *   `display` size tier, tight leading + a hair of negative tracking.
 * - `subtitle` — the mono small-caps wide-tracked descriptor below the head:
 *   the `eyebrow` size tier, muted ink, uppercase.
 *
 * `roleInk` lets a premium DARK panel pass already-resolved inks (so the
 * treatment works on a dark instrument card too); defaults are the
 * document-readable neutral inks. The caller supplies the resolved colours
 * (this lib stays free of a roleColor import to avoid a cycle); pass
 * `roleColor(theme, 'accent' | 'ink' | 'muted')`.
 */
export function reportNumberedHeadingStyles(
  theme: Pick<ReportTheme, 'headingFont' | 'headingScale' | 'themeTypographyScale'>,
  inks: { accent: string; ink: string; muted: string },
): ReportNumberedHeadingStyles {
  const sizes = reportTypographySizes(theme);
  return {
    number: {
      fontFamily: 'var(--report-apparatus-font, var(--report-body-font, inherit))',
      fontSize: sizes.eyebrow,
      lineHeight: REPORT_LINE_HEIGHT.eyebrow,
      letterSpacing: REPORT_EYEBROW_TRACKING,
      textTransform: 'uppercase',
      fontWeight: 600,
      color: inks.accent,
    },
    heading: {
      fontFamily: theme.headingFont,
      fontSize: sizes.display,
      lineHeight: REPORT_LINE_HEIGHT.heading,
      letterSpacing: '-0.01em',
      fontWeight: 600,
      color: inks.ink,
    },
    subtitle: {
      fontFamily: 'var(--report-apparatus-font, var(--report-body-font, inherit))',
      fontSize: sizes.eyebrow,
      lineHeight: REPORT_LINE_HEIGHT.eyebrow,
      letterSpacing: REPORT_EYEBROW_TRACKING,
      textTransform: 'uppercase',
      color: inks.muted,
    },
  };
}

// ── N1.5C WRAPPER-FLATTEN — the ONE "open vs boxed" seam ─────────────────────
// The editorial monograph is a flat cream sheet: NO boxed sub-wrappers (no
// border-on-all-sides, no fill, no radius, no shadow). This helper is the SINGLE
// source for the open treatment a boxed child collapses to in editorial mode, so
// "open vs boxed" lives in one place (the exact seam that let boxes survive the
// ClassicSectionShell→EditorialSectionShell sweep). It is the editorial sibling
// of the boxed `dataBoxStyle` / FlowCard / EditorialCardGrid / empty-state
// chrome — each of those gates on `editorialActive` and, when ON, routes its
// container style through here instead of emitting a box.
//
// This lib stays free of a `roleColor` import to avoid a cycle (mirrors
// `reportNumberedHeadingStyles`), so the caller passes the already-resolved
// hairline / rule / accent / muted inks via `inks`.

/** Style + (optional) micro-label tokens for an OPEN (un-boxed) editorial block. */
export interface EditorialFlatStyles {
  /** A hairline TOP-rule open block (the journal-article sub-block treatment). */
  topRule: CSSProperties;
  /** A 3px accent LEFT-rule open block (the empty-state / note treatment). */
  leftRule: CSSProperties;
  /** The run-in mono small-caps wide-tracked micro-label that titles an open block. */
  microLabel: CSSProperties;
}

/**
 * Resolve the OPEN-block styles a boxed editorial child collapses to. `dense`
 * (theme.density === 'compact') tightens the lead. `inks` supplies the resolved
 * `roleColor(theme, 'rule' | 'accent' | 'muted')` tones.
 *
 *  - `topRule`  — `borderTop: 1px solid rule` + `paddingTop` (no fill / radius /
 *    side-borders); the structural sibling of `sectionBlockChrome`'s editorial
 *    `entryStyle`. Used by `dataBoxStyle`, the FlowCard group separators, and
 *    each EditorialCardGrid card top.
 *  - `leftRule` — `borderLeft: 3px solid accent` + `background: transparent`
 *    (the ported `SectionShell` flatten-to-cream empty-state treatment). Used by
 *    the inline empty-states + the elevation-empty note.
 *  - `microLabel` — the mono small-caps label that runs in above an open block
 *    (replaces the boxed FlowCard header / dataBox heading).
 */
export function editorialFlat(
  dense: boolean,
  inks: { rule: string; accent: string; muted: string },
): EditorialFlatStyles {
  return {
    topRule: {
      borderTop: `1px solid ${inks.rule}`,
      paddingTop: dense ? 10 : 14,
    },
    leftRule: {
      borderLeft: `3px solid ${inks.accent}`,
      background: 'transparent',
    },
    microLabel: {
      fontFamily: 'var(--report-apparatus-font, var(--report-body-font, inherit))',
      fontSize: 10.5,
      fontWeight: 600,
      letterSpacing: '0.14em',
      textTransform: 'uppercase',
      color: inks.muted,
    },
  };
}
